<script setup lang="ts">
import Divider from "primevue/divider";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useRecommendationsCollectionsQuery, type Audience } from "@/queries/recommendations";
import Button from "primevue/button";
import DeferredContent from "primevue/deferredcontent";
import RecommendationsMansoryWallSkeleton from "@/widgets/home/Recommended/RecommendationsMansoryWallSkeleton.vue";
import CustomCardBlock from "@/components/UI/CustomCard/CustomCardBlock.vue";
import YellowAiTip from "@/widgets/common/YellowAiTip.vue";
import CustomButton from "@/components/UI/CustomButtom/CustomButton.vue";
import type { ProductCustomCardItem } from "@/types/ProductType";

import { useI18n } from "vue-i18n";
import { useDevice } from "@/composables/useDevice";

const { t } = useI18n();

const router = useRouter();
const route = useRoute();

const DEFAULT_AUDIENCE: Audience = "woman";

const audience = computed<Audience>(() => {
  const value = route.query.audience;
  if (Array.isArray(value)) {
    return (value[0] ?? DEFAULT_AUDIENCE) as Audience;
  }
  return (value ?? DEFAULT_AUDIENCE) as Audience;
});

const { isDevice } = useDevice();
const deviceType = computed(() => isDevice.value);

const width = ref(window.innerWidth);

const updateWidth = () => {
  width.value = window.innerWidth;
};

const collectionsLimit = computed(() => deviceType.value === "mobile" ? 6 : 24);
const collectionsQuery = useRecommendationsCollectionsQuery(collectionsLimit, audience);
const isCollectionsLoading = computed(() => collectionsQuery.isLoading.value);
const collectionsData = computed(() => collectionsQuery.data.value?.items ?? []);

function productClickHandler(id: number, variantId: number) {
  router.push({ path: `/product=${id}/variant=${variantId}` });
}

async function loadCollectionsData() {
  await collectionsQuery.refresh();
}

onMounted(async () => {
  window.addEventListener("resize", updateWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
});
</script>
<template>
  <DeferredContent>
    <div class="mansory-wall-backgrund">
      <div class="recommendation_content_wrapper">
        <div class="recommendation_top_content__wrapper">
          <div class="top-content__text">
            <div class="popular-items__header-title">
              <span class="popular-items_title h1">{{
                t("home_page.popular_today.popular_today_title")
              }}</span>
              <yellow-ai-tip :ai-tip-text="t('ai_tips.products_liked_past_week')" />
            </div>
          </div>
        </div>
        <recommendations-mansory-wall-skeleton v-if="isCollectionsLoading" />
        <div v-else class="mansory-wall-wrapper">
          <div v-for="item in collectionsData" class="recommendation-card-wrapper">
            <CustomCardBlock
              :productData="item"
              @click="productClickHandler(item.id, item.id)"
              @dislike="loadCollectionsData"
            />
          </div>
        </div>
        <div class="show-more-popular-btn desktop">
          <CustomButton
            :label="t('home_page.popular_today.show_more_popular_btn')"
            severity="danger"
            class="btn-styc"
          />
        </div>
        <div class="mobile">
          <Button
            label="Показать больше популярных товаров"
            severity="danger"
            icon="pi pi-arrow-right"
            icon-pos="right"
            class="btn-styc"
          />
        </div>
      </div>
    </div>
  </DeferredContent>
</template>

<style scoped>
.mansory-wall-backgrund {
  width: 100%;
}

.show-more-popular-btn {
  position: sticky;
  bottom: 50px;
  left: 36%;
  width: fit-content;
}

.recommendation-card-wrapper {
  width: calc(100% / 5 - 20px);
}

.top-content__text {
  display: flex;
}

.recommendation_content_wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 100px;
  width: 100%;
  background: linear-gradient(#f1eff5, #e9edf7);
  gap: 40px;
}

.mansory-wall-wrapper {
  display: flex;
  width: 90%;
  align-content: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 40px;
}

.recommendation_top_content__wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 90%;
}

.popular-items__header-title {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

.popular-items_title {
  color: rgba(35, 47, 69, 1);
}

:deep(.masonry-wall) {
  max-height: 4500px;
  overflow: hidden;
}

.mobile {
  display: none;
}

@media screen and (max-width: 1440px) {
  .desktop {
    display: none;
  }

  .mobile {
    display: initial;
  }

  .mansory-wall-wrapper {
    max-height: none;
  }

  .recommendation_content_wrapper {
    padding-bottom: 20px;
  }
}

@media screen and (max-width: 1024px) {
  .desktop {
    display: none;
  }

  .mobile {
    display: initial;
  }

  .mansory-wall-wrapper {
    max-height: none;
  }

  .recommendation_content_wrapper {
    padding-bottom: 20px;
  }

  .recommendation-card-wrapper {
    width: calc(100% / 4 - 20px);
  }
}

@media screen and (max-width: 1920px) {
  .mansory-wall-wrapper {
    gap: 20px;
  }
}

@media screen and (max-width: 1366px) {
  .mansory-wall-wrapper {
    gap: 20px;
  }
}

@media screen and (max-width: 1024px) {
  .mansory-wall-wrapper {
    gap: 20px;
  }
}

@media screen and (max-width: 768px) {
  .mansory-wall-wrapper {
    gap: 8px;
  }

  .recommendation-card-wrapper {
    width: calc(100% / 2 - 10px);
  }
}
</style>
